import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { fromEvent, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { AdvancedSearchDraft } from 'src/app/models/advanced-search';
import { INavpanelAction, NAVPANEL_ACTION_TYPE } from 'src/app/models/card';
import { AuthpopupComponent } from 'src/app/shared/components/authpopup/authpopup.component';
import { FirebaseAuthService } from 'src/app/shared/providers/firebase-auth.service';
import { AdvancedSearchNavbarComponent } from '../advanced-search-navbar/advanced-search-navbar.component';
import { AiSearchService } from '../ai-search.service';
import { WebaccountComponent } from '../webaccount/webaccount.component';
import { WebNavService } from '../webnavigation.service';
import {
  DISCOVER_SEARCH_LIMIT,
  FEATURED_CARD_COUNT,
  FeaturedBlock,
  FeaturedCard,
  featuredFromCollection,
  featuredFromSearch,
  featuredSearchFailed,
  featuredSearching,
} from './discover-featured-block';
import { ReferenceRequestDialogComponent, ReferenceRequestDialogData } from './reference-request-dialog/reference-request-dialog.component';
import {
  DISCOVER_COLLECTION_COVERS,
  DISCOVER_DEFAULT_FEATURED,
  DISCOVER_FAQ,
  DISCOVER_FEATURED,
  DISCOVER_HERO,
  DISCOVER_TRY_QUERIES,
  DiscoverCollectionCover,
  DiscoverFeaturedCollection,
  DiscoverFeaturedId,
  DiscoverImage,
  discoverImageUrl,
} from './discover-landing.data';

// The reference-discovery landing. Layout and copy follow the designer's Figma frame
// "Advertising_web_last" (docs/landing-discover-port.md has the section map and the history
// back to the boss's prototype). The "Try …" chips, the hero link and the covers show one of
// four bundled collections in the featured block; a typed search (hero form or the dock) asks
// the public search endpoint and shows its first seven results there instead, with ?q in the
// URL as the source of truth. "Explore all" / a card hand the query to /library?q=… — behind
// sign-in for guests (owner, 2026-09-18).
@Component({
  selector: 'app-discover-landing',
  templateUrl: './discover-landing.component.html',
  styleUrls: ['./discover-landing.component.scss'],
})
export class DiscoverLandingComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('heroSearch', { static: true }) private heroSearchRef?: ElementRef<HTMLElement>;
  @ViewChild('featuredSection', { static: true }) private featuredSectionRef?: ElementRef<HTMLElement>;
  @ViewChild('keepSection', { static: true }) private keepSectionRef?: ElementRef<HTMLElement>;
  @ViewChild('dockNote', { static: true }) private dockNoteRef?: ElementRef<HTMLElement>;
  @ViewChild('dockNoteText', { static: true }) private dockNoteTextRef?: ElementRef<HTMLElement>;
  @ViewChild(AdvancedSearchNavbarComponent) private dockRef?: AdvancedSearchNavbarComponent;

  public readonly hero = DISCOVER_HERO;
  public readonly covers = DISCOVER_COLLECTION_COVERS;
  public readonly tryQueries = DISCOVER_TRY_QUERIES;
  public readonly faq = DISCOVER_FAQ;

  // The featured block shows one thing at a time: a bundled collection or a search. It is
  // rendered through a one-item list so a switch re-creates the block (and runs its fade-in)
  // instead of swapping images in place; a search updates in place (skeletons → cards).
  public featured: FeaturedBlock = featuredFromCollection(DiscoverLandingComponent.featuredById(DISCOVER_DEFAULT_FEATURED));
  public featuredList: FeaturedBlock[] = [this.featured];
  // No fade on the initial render — only on a switch the user asked for.
  public featuredSwitched = false;
  public readonly skeletonSlots = Array.from({ length: FEATURED_CARD_COUNT }, (_, index) => index);

  public readonly searchPlaceholder = 'What are you creating?';

  public heroPrompt = '';

  // The search the featured block is showing ('' while it shows a bundled collection).
  private currentQuery = '';
  // Set by a submit: the block scrolls on screen once the search starts. Not on a page load
  // with ?q, and not when "back" brings a query back.
  private scrollOnSearch = false;
  private searchSubscription?: Subscription;
  private queryParamsSubscription?: Subscription;

  // Brief the user typed in the request dialog and closed without sending — comes back when
  // the dialog reopens (the prototype's requestDraft).
  private requestDraft = '';

  // The dock repeats the hero search, so it stays away while the hero form is on screen and
  // fades in once the user scrolls past it (designer's call). Starts hidden: the first screen
  // has the form, and a dock that pops up and fades out again on load would look like a glitch.
  public dockVisible = false;

  // The note under the dock is white; while it floats over the light "keep" section it turns
  // dark (designer's call) — except over that section's cover images, which are dark renders.
  // Only the one light section exists, so that is what gets watched.
  public dockNoteOnLight = false;

  private heroSearchObserver?: IntersectionObserver;
  private dockNoteObserver?: IntersectionObserver;
  private resizeSubscription?: Subscription;
  private accountPopupSubscription?: Subscription;

  constructor(
    private dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
    private firebaseAuth: FirebaseAuthService,
    private webNavService: WebNavService,
    private aiSearch: AiSearchService,
  ) {}

  ngOnInit(): void {
    // No IntersectionObserver (ancient browsers) → the dock simply stays on.
    this.dockVisible = !DiscoverLandingComponent.canObserve();

    // ?q drives the search: a submit writes it, a reload or "back" replays it.
    this.queryParamsSubscription = this.route.queryParamMap.subscribe((params) => {
      const query = DiscoverLandingComponent.normalizeQuery(params.get('q') || '');
      if (query) {
        this.syncPrompts(query);
        this.runSearch(query);
      } else if (this.currentQuery) {
        // "Back" from a search to the plain page.
        this.currentQuery = '';
        this.searchSubscription?.unsubscribe();
        this.syncPrompts('');
        this.showBlock(featuredFromCollection(DiscoverLandingComponent.featuredById(DISCOVER_DEFAULT_FEATURED)));
      }
    });
  }

  ngAfterViewInit(): void {
    // The dock did not exist when ?q was read; setting its prompt inside this hook would change
    // a checked binding (NG0100), so it waits a tick.
    if (this.currentQuery) {
      const query = this.currentQuery;
      setTimeout(() => this.dockRef?.setPrompt(query));
    }
    this.observeHeroSearch();
    this.observeDockNoteSurface();
    // The note strip is measured in px, so a resize (or the phone breakpoint) rebuilds it.
    this.resizeSubscription = fromEvent(window, 'resize')
      .pipe(debounceTime(150))
      .subscribe(() => this.observeDockNoteSurface());
  }

  ngOnDestroy(): void {
    this.stopScroll?.();
    this.searchSubscription?.unsubscribe();
    this.queryParamsSubscription?.unsubscribe();
    this.heroSearchObserver?.disconnect();
    this.dockNoteObserver?.disconnect();
    this.resizeSubscription?.unsubscribe();
    this.accountPopupSubscription?.unsubscribe();
  }

  public get isSignedIn(): boolean {
    return !!this.firebaseAuth.currentUser;
  }

  // Second line of the card hover caption (the library says "Open preview" there). A click on a
  // card is gated by sign-in, so the hint says what will actually happen.
  public get cardHint(): string {
    return this.isSignedIn ? 'Open in Library' : 'Sign in to open';
  }

  public imageUrl(image: DiscoverImage): string {
    return discoverImageUrl(image);
  }

  public trackByCardSrc(index: number, card: FeaturedCard): string {
    return card.src;
  }

  public trackByCoverName(index: number, cover: DiscoverCollectionCover): string {
    return cover.name;
  }

  public trackByFeaturedKey(index: number, block: FeaturedBlock): string {
    return block.key;
  }

  // --- featured block: bundled collections (chips, hero link) and search results ---

  // Shows the collection in the featured block and brings the block on screen (the chips sit
  // in the hero, the block is below the fold). Same collection again just scrolls. A chip also
  // ends the current search: ?q goes, so a reload shows the page, not stale results.
  public showCollection(id: DiscoverFeaturedId, event?: Event): void {
    event?.preventDefault();

    if (this.currentQuery) {
      this.currentQuery = '';
      this.searchSubscription?.unsubscribe();
      this.router.navigate([], { relativeTo: this.route, queryParams: { q: null }, queryParamsHandling: 'merge', replaceUrl: true });
    }
    this.showBlock(featuredFromCollection(DiscoverLandingComponent.featuredById(id)));
    this.scrollToFeatured();
  }

  // "Explore all N references", the cards and the covers open the query in the library, which
  // is behind sign-in for now (designer, 2026-09-18): guests sign in first and go on to
  // /library?q=… once they have; users go straight there.
  public onOpenInLibraryClick(query: string, event?: Event): void {
    event?.preventDefault();

    if (!this.isSignedIn) {
      this.openAuthPopup().afterClosed().subscribe(() => {
        if (this.isSignedIn) {
          this.goToLibrary(query);
        }
      });
      return;
    }

    this.goToLibrary(query);
  }

  public onRetrySearchClick(): void {
    if (this.currentQuery) {
      this.runSearch(this.currentQuery);
    }
  }

  // --- new-reference request ---

  // "Request references": the brief dialog, prefilled with the draft or whatever is in the
  // hero search. Sign-in happens inside the dialog; the draft survives a plain close.
  public onRequestClick(): void {
    const data: ReferenceRequestDialogData = {
      brief: this.requestDraft || this.heroPrompt,
      context: {
        source: 'discover',
        collection: this.featured.name || undefined,
        query: this.currentQuery || undefined,
      },
    };
    const dialogRef = this.dialog.open(ReferenceRequestDialogComponent, {
      data,
      restoreFocus: false,
      panelClass: ['webpopup-overlay', 'web-reference-request'],
      ariaLabelledBy: 'reference-request-title',
    });

    // beforeClosed: the component is still alive here, afterClosed it is gone.
    dialogRef.beforeClosed().subscribe(() => {
      const instance = dialogRef.componentInstance;
      this.requestDraft = instance && !instance.sent ? instance.brief : '';
    });
  }

  // --- search: hero form and dock submit the same way ---

  public onHeroSearchSubmit(event?: Event): void {
    event?.preventDefault();
    this.search(this.heroPrompt);
  }

  public onDockSearchSubmit(draft: AdvancedSearchDraft): void {
    this.search(draft?.prompt || '');
  }

  // Puts the query in ?q; the route subscription runs it. The same query again (or a retry
  // after an error) reruns it directly, since the URL does not change.
  private search(prompt: string): void {
    const query = DiscoverLandingComponent.normalizeQuery(prompt);
    if (!query) {
      return;
    }

    this.syncPrompts(query);
    this.scrollOnSearch = true;

    if (query === this.currentQuery) {
      this.runSearch(query);
      return;
    }
    this.router.navigate([], { relativeTo: this.route, queryParams: { q: query }, queryParamsHandling: 'merge' });
  }

  private runSearch(query: string): void {
    this.currentQuery = query;
    this.searchSubscription?.unsubscribe();
    this.showBlock(featuredSearching(query));
    if (this.scrollOnSearch) {
      this.scrollOnSearch = false;
      this.scrollToFeatured();
    }

    this.searchSubscription = this.aiSearch.search(query, DISCOVER_SEARCH_LIMIT).subscribe({
      next: (response) => {
        if (this.currentQuery === query) {
          this.showBlock(featuredFromSearch(query, response));
        }
      },
      error: (error) => {
        if (this.currentQuery === query) {
          console.error('Discover search failed', error);
          this.showBlock(featuredSearchFailed(query));
        }
      },
    });
  }

  // The hero form and the dock show the same query, whichever one it came from.
  private syncPrompts(query: string): void {
    this.heroPrompt = query;
    this.dockRef?.setPrompt(query);
  }

  private showBlock(block: FeaturedBlock): void {
    if (block.key !== this.featured.key) {
      this.featuredSwitched = true;
    }
    this.featured = block;
    this.featuredList = [block];
  }

  private scrollToFeatured(): void {
    const section = this.featuredSectionRef?.nativeElement;
    if (!section) {
      return;
    }
    // Our own easing rather than scrollIntoView({ behavior: 'smooth' }): src/js/smoothscroll.js
    // overrides smooth scrolling app-wide and throws outside the collection-details screen.
    const margin = parseFloat(getComputedStyle(section).scrollMarginTop) || 0;
    this.scrollWindowTo(section.getBoundingClientRect().top + window.scrollY - margin);
  }

  private static normalizeQuery(prompt: string): string {
    return `${prompt || ''}`.replace(/\s+/g, ' ').trim().slice(0, 120);
  }

  // --- topbar + dock ---

  public onDockLibraryClick(): void {
    if (!this.isSignedIn) {
      this.openAuthPopup();
      return;
    }

    this.router.navigateByUrl('/library');
  }

  public onAccountClick(): void {
    if (!this.isSignedIn) {
      this.openAuthPopup();
      return;
    }

    this.openAccountPopup();
  }

  // --- dock visibility ---

  // Share of the hero form that still counts as "on screen". Below it the dock comes in — the
  // designer wants it before the form is gone completely, not after.
  private static readonly HERO_SEARCH_VISIBLE_RATIO = 0.5;

  private static canObserve(): boolean {
    return typeof IntersectionObserver !== 'undefined';
  }

  private static featuredById(id: DiscoverFeaturedId): DiscoverFeaturedCollection {
    return DISCOVER_FEATURED.find((collection) => collection.id === id) || DISCOVER_FEATURED[0];
  }

  private observeHeroSearch(): void {
    const form = this.heroSearchRef?.nativeElement;
    if (!form || !DiscoverLandingComponent.canObserve()) {
      return;
    }

    // The dock is the bottom ~90px of the viewport; a form still tucked under that strip does
    // not count as "on screen", so the dock does not vanish before the form can be seen.
    const ratio = DiscoverLandingComponent.HERO_SEARCH_VISIBLE_RATIO;
    this.heroSearchObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        this.dockVisible = entry.intersectionRatio < ratio;
      },
      { threshold: [ratio], rootMargin: '0px 0px -88px 0px' },
    );
    this.heroSearchObserver.observe(form);
  }

  // Watches what reaches the strip the fixed note sits in (from the note's top edge down to the
  // viewport bottom; the strip is the observer's root via rootMargin): the light section puts
  // the note on dark text, any of its cover collages under the text puts it back on white.
  private observeDockNoteSurface(): void {
    this.dockNoteObserver?.disconnect();
    this.dockNoteObserver = undefined;

    const note = this.dockNoteRef?.nativeElement;
    const text = this.dockNoteTextRef?.nativeElement;
    const light = this.keepSectionRef?.nativeElement;
    // On phones the note is back in the flow (see the SCSS) and has nothing to adapt to.
    if (!note || !text || !light || !DiscoverLandingComponent.canObserve() || getComputedStyle(note).position !== 'fixed') {
      this.dockNoteOnLight = false;
      return;
    }

    // Layout position, not getBoundingClientRect(): the hidden note is translated 18px down.
    const bottomOffset = parseFloat(getComputedStyle(note).bottom) || 0;
    const stripTop = Math.max(0, Math.round(window.innerHeight - bottomOffset - note.offsetHeight));
    // The <p> spans the viewport; the text inside is what must not land on a cover.
    const { left: textLeft, right: textRight } = text.getBoundingClientRect();
    const covers = Array.from(light.querySelectorAll<HTMLElement>('.cover__collage'));

    let onLightSection = false;
    const coversUnderText = new Set<Element>();
    this.dockNoteObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.target === light) {
            onLightSection = entry.isIntersecting;
            continue;
          }
          const box = entry.boundingClientRect;
          if (entry.isIntersecting && box.right > textLeft && box.left < textRight) {
            coversUnderText.add(entry.target);
          } else {
            coversUnderText.delete(entry.target);
          }
        }
        this.dockNoteOnLight = onLightSection && coversUnderText.size === 0;
      },
      { threshold: 0, rootMargin: `-${stripTop}px 0px 0px 0px` },
    );
    this.dockNoteObserver.observe(light);
    covers.forEach((cover) => this.dockNoteObserver?.observe(cover));
  }

  private static readonly SCROLL_DURATION_MS = 450;

  // Cancels the running eased scroll (rAF + the user-input listeners), if any.
  private stopScroll?: () => void;

  // Eased window scroll (450 ms, ease-in-out). Jumps under prefers-reduced-motion; stops as soon
  // as the user scrolls on their own.
  private scrollWindowTo(targetY: number): void {
    this.stopScroll?.();

    const maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    const endY = Math.min(Math.max(0, Math.round(targetY)), maxY);
    const startY = window.scrollY;
    const reduceMotion = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion || Math.abs(endY - startY) < 2) {
      window.scrollTo(0, endY);
      return;
    }

    const startedAt = performance.now();
    let frame = 0;
    const stop = (): void => {
      cancelAnimationFrame(frame);
      window.removeEventListener('wheel', stop);
      window.removeEventListener('touchstart', stop);
      this.stopScroll = undefined;
    };
    const step = (now: number): void => {
      const t = Math.min(1, (now - startedAt) / DiscoverLandingComponent.SCROLL_DURATION_MS);
      const eased = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      window.scrollTo(0, Math.round(startY + (endY - startY) * eased));
      if (t < 1) {
        frame = requestAnimationFrame(step);
      } else {
        stop();
      }
    };

    window.addEventListener('wheel', stop, { passive: true });
    window.addEventListener('touchstart', stop, { passive: true });
    this.stopScroll = stop;
    frame = requestAnimationFrame(step);
  }

  private goToLibrary(prompt: string): void {
    const q = DiscoverLandingComponent.normalizeQuery(prompt);
    this.router.navigate(['/library'], { queryParams: q ? { q } : {} });
  }

  private openAuthPopup(): MatDialogRef<AuthpopupComponent> {
    return this.dialog.open(AuthpopupComponent, { data: {}, restoreFocus: false, panelClass: 'webpopup-overlay' });
  }

  // Same account popup as the library. Settings and sign-out are handled here; the
  // subscription actions belong to the library flow (and the boss keeps the subscription out of
  // this funnel), so they just take the user there.
  private openAccountPopup(): void {
    const dialogRef = this.dialog.open(WebaccountComponent, { restoreFocus: false, panelClass: ['webpopup-overlay', 'web-account'] });

    this.accountPopupSubscription?.unsubscribe();
    this.accountPopupSubscription = dialogRef.componentInstance.webAccountPopupEvent.subscribe((action: INavpanelAction) => {
      switch (action.actionType) {
        case NAVPANEL_ACTION_TYPE.SETTINGS:
          this.router.navigateByUrl('/service');
          break;
        case NAVPANEL_ACTION_TYPE.SIGN_OUT:
          this.webNavService.signOut();
          break;
        default:
          this.router.navigateByUrl('/library');
          break;
      }
    });

    dialogRef.afterClosed().subscribe(() => {
      this.accountPopupSubscription?.unsubscribe();
      this.accountPopupSubscription = undefined;
    });
  }
}
